/**
 * HOTFIX: Host-Aware Routing Context.
 *
 * Detects `window.location.hostname` and classifies the current host as:
 *   - "platform": app.dynicty.com or preview/dev host → render Owner Portal.
 *   - "tenant":   *.dynicty.com (non-reserved) → render Tenant Storefront.
 *   - "unknown":  bare apex / unrelated domain → NotFound.
 *
 * Uses existing `/api/public/tenant-by-host` endpoint (zero new engine).
 * Kept small — pure context; no business logic.
 */
import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const HostContext = createContext({
  kind: "platform",
  host: "",
  tenant: null,
  loading: true,
  error: null,
});

const PLATFORM_APEX = "dynicty.com";
const PLATFORM_HOST = "app.dynicty.com";
const RESERVED_SUBDOMAINS = new Set(["app", "api", "admin", "www", "cdn", "static", "media", "assets"]);
const DEV_HOST_SUFFIXES = [".preview.emergentagent.com", ".emergentagent.com", "localhost", "127.0.0.1"];

function classifyHost(host) {
  const h = (host || "").toLowerCase();
  if (h === PLATFORM_HOST) return { kind: "platform" };
  for (const suf of DEV_HOST_SUFFIXES) {
    if (h === suf.replace(/^\./, "") || h.endsWith(suf)) {
      return { kind: "platform" };
    }
  }
  const apexSuffix = "." + PLATFORM_APEX;
  if (!h.endsWith(apexSuffix)) return { kind: "unknown" };
  const label = h.slice(0, -apexSuffix.length);
  if (!label || label.includes(".")) return { kind: "unknown" };
  if (RESERVED_SUBDOMAINS.has(label)) return { kind: "platform" };
  if (!/^[a-z0-9][a-z0-9-]{2,39}$/.test(label)) return { kind: "unknown" };
  return { kind: "tenant", slug: label };
}

function isDevHost(host) {
  const h = (host || "").toLowerCase();
  return DEV_HOST_SUFFIXES.some((suf) => h === suf.replace(/^\./, "") || h.endsWith(suf));
}

// DEV-ONLY tenant simulation: pada preview/localhost, `?__tenant=<slug>` (atau
// localStorage `dynicty_dev_tenant`) mensimulasikan tenant host untuk E2E testing.
// TIDAK berlaku di production host (app.dynicty.com / *.dynicty.com).
// `?__tenant=off` menghapus simulasi.
function resolveDevTenantOverride(host) {
  if (typeof window === "undefined" || !isDevHost(host)) return null;
  try {
    const qp = new URLSearchParams(window.location.search).get("__tenant");
    if (qp === "off") {
      window.localStorage.removeItem("dynicty_dev_tenant");
      return null;
    }
    if (qp) {
      window.localStorage.setItem("dynicty_dev_tenant", qp);
      return qp;
    }
    return window.localStorage.getItem("dynicty_dev_tenant");
  } catch {
    return null;
  }
}

export function HostProvider({ children }) {
  const realHost = typeof window !== "undefined" ? window.location.hostname : "";
  const devTenant = resolveDevTenantOverride(realHost);
  const host = devTenant ? `${devTenant}.${PLATFORM_APEX}` : realHost;
  const classified = devTenant ? { kind: "tenant", slug: devTenant } : classifyHost(realHost);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(classified.kind === "tenant");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (classified.kind !== "tenant") {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.get("/public/tenant-by-host", { params: { host } });
        if (!cancelled) setTenant(r.data);
      } catch (e) {
        if (!cancelled) {
          const status = e?.response?.status;
          setError(status === 404 ? "not-found" : "network");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [classified.kind, host]);

  return (
    <HostContext.Provider
      value={{
        kind: classified.kind,
        slug: classified.slug || null,
        host,
        tenant,
        loading,
        error,
        platformApex: PLATFORM_APEX,
        platformHost: PLATFORM_HOST,
      }}
    >
      {children}
    </HostContext.Provider>
  );
}

export function useHost() {
  return useContext(HostContext);
}

export { classifyHost };
